import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '../components/ui/DataTable';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { KnowledgeStatusBadge } from '../components/knowledge/KnowledgeStatusBadge';
import { AddKnowledgeDocumentDialog } from '../components/knowledge/AddKnowledgeDocumentDialog';
import { DocumentKnowledgeBases } from '../components/knowledge/DocumentKnowledgeBases';
import {
  useDeleteKnowledgeDocument,
  useKnowledgeDocuments,
  useKnowledgeMembershipIndex,
} from '../hooks/useKnowledge';
import { useListState } from '../hooks/useListState';
import { useFlag } from '../hooks/useFeatureFlags';
import {
  isAcceptableDraggedType,
  useKnowledgeDocumentUpload,
} from '../hooks/useKnowledgeDocumentUpload';
import PlanRequiredNotice from '../components/ui/PlanRequiredNotice';
import {
  KNOWLEDGE_MAX_DOCUMENT_LABEL,
  KNOWLEDGE_UPLOAD_FORMATS_LABEL,
} from '../config';
import type { KnowledgeDocument, KnowledgeDocumentStatus } from '../types';

/**
 * The account's document library — the primary knowledge surface (task 574,
 * kv's 2026-08-17 model revision).
 *
 * Documents are first class: upload or paste one and it exists, with no
 * knowledge base involved. Bases are groupings shown here only as the
 * memberships column, editable in place. Ingestion status lives here too, since
 * this is where a User goes to find out whether their document is usable yet.
 */
const STATUS_FILTERS: Array<{ value: '' | KnowledgeDocumentStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Queued' },
  { value: 'indexing', label: 'Indexing' },
  { value: 'ready', label: 'Ready' },
  { value: 'failed', label: 'Failed' },
];

function formatSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sourceLabel(document: KnowledgeDocument): string {
  if (document.sourceType === 'text') return 'Pasted text';
  return document.extension ? document.extension.toUpperCase() : 'File';
}

export default function KnowledgeDocumentsPage() {
  const canCreate = useFlag('innovate.fe.knowledge.documents.create');
  const basesPageEnabled = useFlag('innovate.fe.knowledge.bases.page');

  const { page, limit, search, setPage, setLimit, setSearch } = useListState({
    entityType: 'knowledge-documents',
  });
  const [statusFilter, setStatusFilter] = useState<'' | KnowledgeDocumentStatus>('');
  const [isAddOpen, setIsAddOpen] = useState(false);

  const { data, isLoading, error } = useKnowledgeDocuments(page, limit, {
    search: search || undefined,
    status: statusFilter || undefined,
  });
  const documents = data?.data ?? [];
  const pagination = data?.pagination;

  const { bases, index: membershipIndex } = useKnowledgeMembershipIndex();
  const deleteDocument = useDeleteKnowledgeDocument();

  /**
   * THE PAGE IS THE DROP ZONE (task 1084). Dragging files anywhere over the
   * page shows the overlay; dropping uploads them one after another through
   * the shared sequence, so a repeat name gets the same prompt as everywhere
   * else and a plan refusal lands in the banner below the header.
   */
  const planNoticesEnabled = useFlag('innovate.fe.plan.no-plan-notices');
  const [uploadNeedsPlan, setUploadNeedsPlan] = useState(false);
  const {
    upload,
    uploading,
    error: uploadError,
    clearError: clearUploadError,
    conflictDialog,
  } = useKnowledgeDocumentUpload({
    surface: 'documents-page',
    onPlanError: () => setUploadNeedsPlan(true),
  });
  /** null = no file drag in progress; otherwise whether every dragged item may be accepted. */
  const [dragAccepts, setDragAccepts] = useState<boolean | null>(null);
  // Enter/leave fire for every child the pointer crosses; a depth count is what
  // tells leaving the page from moving between rows.
  const dragDepth = useRef(0);

  // The Add document dialog has its own drop zone, and one batch at a time
  // keeps a second drop from talking over an open conflict prompt.
  const pageDropEnabled = canCreate && !isAddOpen && !uploading;

  const isFileDrag = (event: React.DragEvent) =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files');

  const handlePageDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!pageDropEnabled || !isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    const items = Array.from(event.dataTransfer.items ?? []);
    setDragAccepts(items.every((item) => isAcceptableDraggedType(item.type)));
  };

  const handlePageDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!pageDropEnabled || !isFileDrag(event)) return;
    // `dragover` must be prevented for a drop to fire at all.
    event.preventDefault();
  };

  const handlePageDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!pageDropEnabled || !isFileDrag(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragAccepts(null);
  };

  const handlePageDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!pageDropEnabled || !isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragAccepts(null);
    setUploadNeedsPlan(false);
    // One request per file: the route takes a single file.
    for (const file of Array.from(event.dataTransfer.files ?? [])) {
      await upload(file);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          Error loading documents. Please try again.
        </div>
      </div>
    );
  }

  const actions = canCreate ? (
    <button
      type="button"
      data-testid="knowledge-add-document"
      onClick={() => setIsAddOpen(true)}
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      Add document
    </button>
  ) : undefined;

  return (
    <div
      data-testid="knowledge-documents-page"
      className="relative"
      onDragEnter={handlePageDragEnter}
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
    >
      {dragAccepts !== null && (
        <div
          data-testid="knowledge-drop-overlay"
          data-accepts={dragAccepts ? 'true' : 'false'}
          className={`pointer-events-none fixed inset-0 z-40 flex items-center justify-center border-4 border-dashed ${
            dragAccepts
              ? 'border-indigo-500 bg-indigo-50/80 dark:bg-indigo-900/60'
              : 'border-red-500 bg-red-50/80 dark:bg-red-900/60'
          }`}
        >
          <p
            className={`text-lg font-medium ${
              dragAccepts ? 'text-indigo-700 dark:text-indigo-200' : 'text-red-700 dark:text-red-200'
            }`}
          >
            {dragAccepts
              ? 'Drop files to add to your library.'
              : `Only ${KNOWLEDGE_UPLOAD_FORMATS_LABEL} files can be added.`}
          </p>
        </div>
      )}
      <PageHeader
        title="Documents"
        description={
          <>
            Your account&apos;s library. Upload a file or paste text; group documents into
            knowledge bases to decide which agents can draw on them.{' '}
            <span className="text-gray-500 dark:text-gray-400">
              {KNOWLEDGE_UPLOAD_FORMATS_LABEL} · up to {KNOWLEDGE_MAX_DOCUMENT_LABEL} per document.
            </span>
          </>
        }
        actions={actions}
      />

      {/* Why a dropped file did not land (task 1084): a plan refusal gets the
          same needs-a-plan prompt the sidebar shows; anything else is the
          message, dismissible. */}
      {uploadError && (
        <div
          data-testid="knowledge-upload-banner"
          className="mt-4 flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300"
        >
          <span className="min-w-0 flex-1">
            {uploadNeedsPlan && planNoticesEnabled ? <PlanRequiredNotice /> : uploadError}
          </span>
          <button
            type="button"
            onClick={() => {
              clearUploadError();
              setUploadNeedsPlan(false);
            }}
            aria-label="Dismiss this message"
            className="flex-shrink-0 rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/60"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} placeholder="Search documents..." />
            </div>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as '' | KnowledgeDocumentStatus);
                setPage(1);
              }}
              className="block w-44 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading documents...</div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="text-gray-500 dark:text-gray-400 mb-4 mt-4">
              {search || statusFilter
                ? 'No documents match these filters.'
                : 'No documents yet. Add one so your agents have something to draw on.'}
            </div>
            {!search && !statusFilter && canCreate && (
              <button
                type="button"
                onClick={() => setIsAddOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                Add your first document
              </button>
            )}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <TableHeader className="w-1/4">Document</TableHeader>
              <TableHeader className="w-24">Source</TableHeader>
              <TableHeader className="w-32">Status</TableHeader>
              <TableHeader className="w-1/4">Knowledge bases</TableHeader>
              <TableHeader className="w-24">Size</TableHeader>
              <TableHeader className="w-20">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {documents.map((document) => (
                <DataTableRow key={document.id}>
                  <DataTableCell>
                    <div className="min-w-0">
                      {/* Opens the document page (task 1081). */}
                      <Link
                        to={`/knowledge-documents/${document.id}`}
                        data-testid={`knowledge-document-name-${document.id}`}
                        className="block text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 truncate"
                      >
                        {document.name}
                      </Link>
                      {document.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {document.description}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {sourceLabel(document)}
                  </DataTableCell>
                  <DataTableCell>
                    <KnowledgeStatusBadge document={document} />
                  </DataTableCell>
                  <DataTableCell>
                    <DocumentKnowledgeBases
                      documentId={document.id}
                      memberships={membershipIndex.get(document.id) ?? []}
                      allBases={bases}
                    />
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatSize(document.sizeBytes)}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                    {/* The one delete affordance (task 864). A document is not
                        an entity anything can BLOCK on, so it carries no
                        dependents read — the warning is about reach, not about
                        refusal. */}
                    <DeleteResourceControl
                      flag="innovate.fe.knowledge.documents.delete"
                      entityLabel="document"
                      name={document.name}
                      id={document.id}
                      body={
                        <p>
                          This removes the document everywhere: it disappears from every knowledge
                          base it belongs to, and agents stop being able to draw on it. To take it
                          out of one grouping instead, remove it from that knowledge base.
                        </p>
                      }
                      onDelete={() => deleteDocument.mutateAsync(document.id)}
                    />
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        {basesPageEnabled && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Manage groupings on the{' '}
            <Link
              to="/knowledge-bases"
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
            >
              Knowledge Bases
            </Link>{' '}
            page.
          </p>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination pagination={pagination} onPageChange={setPage} onLimitChange={setLimit} />
          </div>
        )}
      </div>

      <AddKnowledgeDocumentDialog isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />
      {conflictDialog}

    </div>
  );
}
